/**
 * The buyer's own relationship to a partial sale listing.
 *
 * Pure, so the rules that decide whether Express Interest is offered can be tested
 * without a renderer. Shared rather than inlined: the drawer and the marketplace
 * both need this, and the last thing both hand-rolled (the sale_status badge)
 * drifted apart — see ./partialSaleStatus.
 *
 * Reads PartialSaleListingType#current_user_interest_status, which is the viewer's
 * OWN PartialSaleListingUser status (expressed_interest / interested /
 * not_interested) or null when they have none.
 */

export const INTEREST_NONE = 'none';
export const INTEREST_PENDING = 'pending';
export const INTEREST_APPROVED = 'approved';
export const INTEREST_DECLINED = 'declined';

const BY_STATUS = {
  expressed_interest: INTEREST_PENDING,
  interested: INTEREST_APPROVED,
  not_interested: INTEREST_DECLINED,
};

/**
 * @param {string|null|undefined} interestStatus - current_user_interest_status
 * @returns {string} one of the INTEREST_* constants
 */
export const buyerInterestState = (interestStatus) => BY_STATUS[interestStatus] || INTEREST_NONE;

/**
 * Whether to offer Express Interest on this listing.
 *
 * Three independent reasons not to: it is not live, it is yours, or you already
 * have a relationship to it (including a decline — a declined buyer re-submitting
 * would just be re-asking a seller who already said no).
 *
 * @param {object} listing - needs sale_status and current_user_interest_status
 * @param {object} opts
 * @param {boolean} opts.isOwnListing - the viewer owns this listing
 * @returns {boolean}
 */
export const canExpressInterest = (listing, { isOwnListing } = {}) => {
  if (!listing || isOwnListing) return false;
  if (listing.sale_status !== 'open') return false;
  return buyerInterestState(listing.current_user_interest_status) === INTEREST_NONE;
};

export default buyerInterestState;
